import { useMemo } from 'react';

import { FeedbackChips } from '@/components/FeedbackChips';
import { useRunContents } from '@/hooks/useSwr';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { RunInputsAndOutputs } from '../Run/components/RunInputsAndOutputs';

export function ConversationTurn({
  run,
  idx,
  plainOptions,
  rootRuns,
}: {
  run: RunSchema;
  idx: number;
  plainOptions: {
    input:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
    output:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
  };
  rootRuns: RunSchema[];
}) {
  const { contents } = useRunContents({
    run: { data: run, isLoading: false },
    withContents: true,
  });

  const inputs = contents?.inputs.data ?? run.inputs;

  const outputs = contents?.outputs.data ?? run.outputs;
  const error = contents?.error.data ?? run.error;
  const updatedRun = useMemo(() => {
    return {
      ...run,
      inputs,
      outputs,
      error,
    };
  }, [error, inputs, outputs, run]);

  const inputsLoading = contents?.inputs.loading;
  const outputsLoading = contents?.outputs.loading;
  const errorLoading = contents?.error.loading;

  return (
    <div
      key={run.id}
      className={cn('flex flex-col gap-4 p-4', idx % 2 === 1 && 'bg-secondary')}
    >
      <div className="flex justify-between gap-[200px]">
        <div className="text-md flex flex-none items-center gap-4 text-tertiary">
          <div>Turn {idx + 1}</div>
        </div>
        <div className="flex flex-1 flex-wrap justify-end gap-2">
          <FeedbackChips
            feedbackStats={run.feedback_stats}
            feedbackSourceRunId={run.id}
          />
        </div>
      </div>
      <RunInputsAndOutputs
        variant={['plain', plainOptions]}
        run={updatedRun}
        prevRun={rootRuns.at(idx - 1)}
        inputsLoading={inputsLoading}
        outputsLoading={outputsLoading}
        errorLoading={errorLoading}
      />
    </div>
  );
}
